import {
	useState,
	useEffect,
	useRef
} from "react";
import {
	Navigate,
	useParams,
	useNavigate
} from "react-router-dom";
import {
	Box,
	Typography,
	Container,
	Grid,
	Paper,
	Skeleton,
	IconButton,
	TextField,
	Checkbox,
	Button,
	Tooltip,
	CircularProgress,
	FormControlLabel,
	Divider,
	Switch,
	Menu,
	MenuItem,
	TableContainer,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import AirlineStopsOutlinedIcon from '@mui/icons-material/AirlineStopsOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import SectionTitle from '../../common/SectionTitle'
import moment from 'moment'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker, DatePicker } from '@mui/x-date-pickers';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import DataTypeRetailTable from './DataTypeRetailTable'
import DataTypeOnOrderTable from './DataTypeOnOrderTable'
import DataFileService from '../../../services/DataFileService'
import MessageContext from '../../../providers/GlobalTip';

const jss = {
	changeLogSection: {
		title: {
			fontSize:16,
			fontWeight:'bold',
			my:2
		},
		actionSection: {
			p:2,
			backgroundColor: '#eee9f3',
			display:'flex',
			alignItems:'center',
		},
	},
	baseInfoSection: {
		px:2,
		py:3,
		display:'flex',
		alignItems: 'center',
		backgroundColor: '#eee9f3',
		dataThrough: {
			width: '230px',
			mr: 2,
		}
	},
	errorSection:{
		p:2,
		backgroundColor:'#f4c7cb',
		color:'error.main',
		title: {
			fontWeight: 'bold',
			fontSize:16,
			pb:1,
		},
		errorItem: {
			py:0.5,
			display:'flex',
			alignItems:'center',
			fontSize:12,
		},
		warningItem: {
			py:0.5,
			display:'flex',
			alignItems:'center',
			fontSize:12,
			color:'warning.main'
		},
		'.MuiSvgIcon-root': {
			fontSize:18,
			mr:1,
		}
	}
}


function DataFileInfo(props) {
	const {dataFile, onDataFileChange, onDataFileDelete} = props
	const [open_more, setOpenMore] = useState(false)
	const more_element = useRef()
	const [dataType, setDataType] = useState('retail')
	const [sortBy, setSortBy] = useState('location')
	const [processing, setProcessing] = useState(false)
	const messageContext = MessageContext.useMessage()

	async function changeDataFileEndOfMonth(e) {
		try {
			await DataFileService.updateDataFile(dataFile.ClientId, dataFile.ClientDataFileId, {
				is_month_to_date: e.target.checked ? 0 : 1
			})
			onDataFileChange({...dataFile, is_month_to_date: e.target.checked ? 0 : 1})
		} catch(e) {
		}
	}

	async function onDataThroughChange(e) {
		try {
			await DataFileService.updateDataFile(dataFile.ClientId, dataFile.ClientDataFileId, {
				data_through_date: moment(e).format('YYYY-MM-DD HH:mm:ss')
			})
			onDataFileChange({...dataFile, data_through_date: moment(e).format('YYYY-MM-DD HH:mm:ss')})
		} catch(e) {
		}
	}

	async function deleteDataFile() {
		await DataFileService.deleteDataFile(dataFile.ClientId, dataFile.ClientDataFileId)
		onDataFileDelete(dataFile)
	}

	async function unfinalize() {
		try {
			await DataFileService.unfinalizeDataFile(dataFile.ClientId, dataFile.ClientDataFileId)
			onDataFileChange({...dataFile, finalized_date: null})
		} catch(e) {
		}
	}

	async function finalize() {
		try {
			const result = await DataFileService.finalizeDataFile(dataFile.ClientId, dataFile.ClientDataFileId)
			onDataFileChange({...dataFile, finalized_date: result.finalized_date})
		} catch(e) {
		}
	}

	async function downloadOriginFile() {
		DataFileService.downloadDataFileOriginData(dataFile.ClientId, dataFile.ClientDataFileId)
	}

	async function processFile() {
		try {
			setProcessing(true)
			const result = await DataFileService.processDataFile(dataFile.ClientId, dataFile.ClientDataFileId)
			onDataFileChange({...dataFile, processed_date: result.processed_date})
			messageContext.showSuccess('Process successfully')
		} catch(e) {
		}
		setProcessing(false)
	}

	return <Box sx={jss.changeLogSection}>
		<SectionTitle 
			label='Data File'
			subLabel={'['+moment(dataFile.Period, 'YYYYMM').format('MMM YYYY')+']'}
			type="primary"
			rightComponent={
				<Box sx={{color:'white'}}>
					{!dataFile.finalized_date && <>
						<FormControlLabel control={<Switch color="success" value={dataFile.is_month_to_date==0} onChange={changeDataFileEndOfMonth} />} label="Is end of month?" />
					</>}
					{dataFile.finalized_date && <>
						{dataFile.is_planned && <>
							<IconButton sx={{color:'white'}} onClick={unfinalize}>
								<LockIcon />
							</IconButton>
						</>}
						{!dataFile.is_planned && <>
							<IconButton sx={{color:'white'}} onClick={unfinalize}>
								<LockOutlinedIcon />
							</IconButton>
						</>}
					</>}
					<IconButton onClick={downloadOriginFile} sx={{color:'white'}}>
						<DownloadRoundedIcon />
					</IconButton>
					<IconButton onClick={e => {setOpenMore(true)}} ref={more_element}>
						<MoreVertOutlinedIcon sx={{color: 'white'}} />
					</IconButton>
					<Menu
						open={open_more}
						anchorEl={more_element.current}
						onClose={e => {setOpenMore(false)}}
					>
						{!dataFile.finalized_date && <MenuItem dense onClick={processFile} disabled={processing}>
							<AirlineStopsOutlinedIcon sx={{fontSize:16,mr:1}}/> Process File
						</MenuItem>}
						{!dataFile.finalized_date && <MenuItem dense onClick={finalize}><LockOutlinedIcon sx={{fontSize:16,mr:1}}/> Finalize File</MenuItem>}
						{dataFile.finalized_date && <MenuItem dense onClick={unfinalize}><LockOpenIcon sx={{fontSize:16,mr:1}}/> UnFinalize File</MenuItem>}
						<Divider />
						<MenuItem dense onClick={deleteDataFile}><DeleteOutlineOutlinedIcon sx={{fontSize:16,mr:1}}/>Delete File</MenuItem>
					</Menu>
				</Box>
			}
		/>

		<Box sx={jss.baseInfoSection}>
		 	{dataFile.is_month_to_date==1 && 
		 		<Box sx={jss.baseInfoSection.dataThrough}>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DesktopDatePicker
							label="Data through date"
							value={dataFile.data_through_date}
							inputFormat="MMM dd"
							mask=""
							onChange={onDataThroughChange}
							renderInput={(params) => <TextField {...params}
								fullWidth={true}
								variant="outlined"
								size="small"
							/>}
						/>
					</LocalizationProvider>
				</Box>
		 	}
			<Box>
				Updated: {moment(dataFile.processed_date).format('MMM/DD HH:mm')}
			</Box>
		</Box>
		
		{dataFile.errors?.length>0 && 
			<Box sx={jss.errorSection}>
				<Typography sx={jss.errorSection.title}>Errors</Typography>
				{dataFile.errors.map((item, index) => {
					return <Box sx={item.W ? jss.errorSection.warningItem : jss.errorSection.errorItem} key={`error_item_${index}`}>
						{item.W && <><WarningAmberIcon /> {item.E}</>}
						{!item.W && <><NewReleasesOutlinedIcon /> {item.E}</>}
					</Box>
				})}
			</Box>
		}

		<Box sx={{p:2,py:3,}}>
			<TextField 
				select
				label="Data Type"
				size="small"
				sx={{width:"200px"}}
				value={dataType}
				onChange={e => setDataType(e.target.value)}
			>
				<MenuItem value="retail">Retail Data</MenuItem>
				<MenuItem value="on_order">On Order</MenuItem>
			</TextField>
			{/*<TextField 
				select
				label="Group By"
				size="small"
				sx={{width:"200px", ml:2}}
				value={sortBy}
				onChange={e => setSortBy(e.target.value)}
			>
				<MenuItem value="class">Classes</MenuItem>
				<MenuItem value="location">Locations</MenuItem>
			</TextField>*/}
			<Box sx={{mt:2}}>
				{dataType=='retail' && 
					<DataTypeRetailTable dataFile={dataFile} sortBy={sortBy}/>
				}
				{dataType=='on_order' && 
					<DataTypeOnOrderTable dataFile={dataFile} sortBy={sortBy}/>
				}
			</Box>
		</Box>

	</Box>
}
export default DataFileInfo